import { useState } from 'react';
import { Box, Button, Menu, MenuButton, MenuItem, MenuList, Text, VStack } from '@chakra-ui/react';
import { MoreVertical, Pencil, Trash2 } from 'lucide-react';
import ModalContainer from '@/components/molecules/ModalContainer';
import { CONTENT_REPORT_REASONS } from '@/constants/contentReportReasons';
import { TContentReportReason } from '@/types/Content.types';
import { background, extra, text } from '@/theme/colors/palettes';

// Overflow menu on a loop item. Authors can edit their own content; "Not interested"
// hard-drops an item from the requester's feeds on the next open.
const LoopOverflowMenu = ({
  onNotInterested,
  onReport,
  onEdit,
  onDelete,
  canReport = true,
  isOnDark = false,
}: {
  onNotInterested: () => void;
  onReport: (reason: TContentReportReason) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  canReport?: boolean;
  isOnDark?: boolean;
}) => {
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const selectReportReason = (reason: TContentReportReason) => {
    onReport(reason);
    setIsReportModalOpen(false);
  };

  return (
    <>
      <Menu placement="bottom-end">
        <MenuButton
          as={Box}
          aria-label="More options"
          cursor="pointer"
          color={isOnDark ? 'whiteAlpha.900' : 'text.mediumBlue'}
          lineHeight={0}
          flexShrink={0}
          _hover={{ opacity: 0.7 }}
        >
          <MoreVertical size={16} />
        </MenuButton>
        <MenuList>
          {onEdit && (
            <MenuItem icon={<Pencil size={16} />} onClick={onEdit}>
              Edit content
            </MenuItem>
          )}
          {onDelete && (
            <MenuItem icon={<Trash2 size={16} />} color={extra.red} fontWeight={600} onClick={onDelete}>
              Delete content
            </MenuItem>
          )}
          {canReport && (
            <MenuItem style={{ color: extra.red, fontWeight: 600 }} onClick={() => setIsReportModalOpen(true)}>
              Report content
            </MenuItem>
          )}
          <MenuItem onClick={onNotInterested}>Not interested</MenuItem>
        </MenuList>
      </Menu>

      <ModalContainer
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        width="420px"
        backgroundColor={background.lightBlue}
        closeButtonColor={text.darkBlue}
      >
        <VStack align="stretch" gap="12px" padding="24px 16px">
          <VStack gap={'4px'}>
            <Text variant="loraSmallTitle" color={text.darkBlue} textAlign="center" paddingX="32px">
              Choose Report Category
            </Text>
            <Text fontSize="14px" color={text.mediumBlue} textAlign="center" paddingBottom="8px">
              Select the reason that best describes the issue.
            </Text>
          </VStack>
          {CONTENT_REPORT_REASONS.map(({ value, label }) => (
            <Button
              key={value}
              variant="outline"
              boxShadow="0px 2px 4px 0px #7C9EBC80"
              justifyContent="flex-start"
              fontSize={'16px'}
              color={text.mediumBlue}
              backgroundColor="white"
              height={'50px'}
              _hover={{ color: 'primary.500' }}
              onClick={() => selectReportReason(value)}
            >
              {label}
            </Button>
          ))}
        </VStack>
      </ModalContainer>
    </>
  );
};

export default LoopOverflowMenu;
